/**
 * Discovery service for user matching and swiping
 */

import { getChatIdForPair } from "@features/messaging/utils/chatParticipants";
import { type PublicUserProfile, PublicUserProfileSchema, type User } from "@fitties/shared";
import { firebaseFunctionsService } from "@infrastructure/firebase/firebaseFunctionsService";
import { backendRegistry } from "@infrastructure/providers/backendRegistry";
import logger from "@logger";
import type { Firestore, Transaction } from "firebase/firestore";
import { doc, runTransaction } from "firebase/firestore";
import type { Match } from "../types";

type UserListRow = { uid?: string; id?: string };

export function resolveDiscoveryUserId(user: UserListRow): string | undefined {
  if (typeof user.uid === "string" && user.uid.length > 0) {
    return user.uid;
  }
  if (typeof user.id === "string" && user.id.length > 0) {
    return user.id;
  }
  return undefined;
}

/**
 * Discovery service class
 */
export class DiscoveryService {
  /**
   * Get users for discovery based on current user's preferences (via Cloud Function).
   */
  async getDiscoveryUsers(
    currentUserId: string,
    limit: number = 20,
    startWithUserId?: string
  ): Promise<PublicUserProfile[]> {
    try {
      logger.info("Fetching discovery users", { currentUserId, limit, startWithUserId });

      const users = await firebaseFunctionsService.callFunctionWithUsersList(
        "getDiscoveryUsers",
        {
          limit,
          ...(startWithUserId ? { startWithUserId } : {}),
        },
        PublicUserProfileSchema
      );
      logger.info("Discovery users fetched successfully", {
        returned: users.length,
      });
      return users;
    } catch (error) {
      logger.error("Error fetching discovery users:", error, {
        cause: error instanceof Error ? error.cause : undefined,
      });
      throw error;
    }
  }

  /**
   * Users who have right-swiped the current user (via Cloud Function).
   */
  async getLikedYouUsers(currentUserId: string): Promise<PublicUserProfile[]> {
    try {
      logger.info("Fetching liked-you users", { currentUserId });
      return await firebaseFunctionsService.callFunctionWithUsersList("getLikedYouUsers", {}, PublicUserProfileSchema);
    } catch (error) {
      logger.error("Error fetching liked-you users:", error, {
        cause: error instanceof Error ? error.cause : undefined,
      });
      throw error;
    }
  }

  /**
   * Record a swipe action.
   * Right swipes call the handleLike Cloud Function (which writes liked_uids server-side).
   * Left swipes write only to the swiper's own rejected_uids array.
   */
  async recordSwipe(swiperId: string, swipedUserId: string, direction: "left" | "right"): Promise<Match | null> {
    try {
      logger.info("📱 Recording swipe", { swiperId, swipedUserId, direction });

      if (direction === "right") {
        return await this.handleRightSwipe(swiperId, swipedUserId);
      }

      await this.handleLeftSwipe(swiperId, swipedUserId);
      return null;
    } catch (error) {
      logger.error("Error recording swipe:", error);
      throw error;
    }
  }

  /**
   * Handle right swipe — delegates to handleLike Cloud Function.
   */
  private async handleRightSwipe(swiperId: string, swipedUserId: string): Promise<Match | null> {
    logger.info("👉 Right swipe — calling handleLike Cloud Function", { swiperId, swipedUserId });

    const result = await firebaseFunctionsService.callFunctionWithData<
      { likedUserId: string },
      { mutual: boolean; chatId?: string }
    >("handleLike", { likedUserId: swipedUserId });

    if (result.data.mutual) {
      const chatId = result.data.chatId ?? getChatIdForPair(swiperId, swipedUserId);
      logger.info("🎉 Mutual match!", { swiperId, swipedUserId, chatId });
      return {
        id: chatId,
        chatId,
        users: [swiperId, swipedUserId],
        created_time: new Date(),
      };
    }

    logger.info("💔 No mutual match yet", { swiperId, swipedUserId });
    return null;
  }

  /**
   * Handle left swipe — writes only to the swiper's own rejected_uids array.
   */
  private async handleLeftSwipe(swiperId: string, swipedUserId: string): Promise<void> {
    const db = backendRegistry.database.getFirestoreInstance() as Firestore;
    const swiperRef = doc(db, "users", swiperId);

    await runTransaction(db, async (transaction: Transaction) => {
      const swiperDoc = await transaction.get(swiperRef);
      if (!swiperDoc.exists()) {
        throw new Error("Current user not found");
      }

      const swiperData = swiperDoc.data() as User;
      const rejected = swiperData.rejected_uids ?? [];
      if (!rejected.includes(swipedUserId)) {
        transaction.update(swiperRef, { rejected_uids: [...rejected, swipedUserId] });
      }
    });
  }
}

// Export singleton instance
export const discoveryService = new DiscoveryService();
export default discoveryService;
